import React, { useEffect, useState } from "react";
import { Box, Divider, IconButton, Paper, styled, Typography } from "@mui/material";
import { DragIndicator } from "@mui/icons-material";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { EntityTemplateField, EntityTemplateStandardField, EntityTemplateStep, SelectableInputField, TemplateEntityType } from "./EntityTemplate.types.ts";
import { useTranslation } from "../localization/useTranslation.ts";
import { ContractFieldRenderer } from "../contract/ContractFieldRenderers.tsx";
import { SystemFieldRenderer } from "./SystemFieldRenderers.tsx";
import { LegalEntityFieldRenderer } from "./LegalEntityFieldRenderers.tsx";
import { IncidentLogFieldRenderer } from "./IncidentLogFieldRenderers.tsx";
import { ProcessFieldRenderer } from "./ProcessFieldRenderers.tsx";
import StepSegment from "../processingActivity/stepSegment/StepSegment.tsx";
import { useUserContext } from "../auth/userContextProvider/UserContextProvider.tsx";
import { Color, DotLegalButton, DotLegalChip, DotLegalSwitch, DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import AddFieldPopover from "./AddFieldPopover.tsx";
import { ComputedFieldTemplateEditing } from "./fields/customFields/computedField.types.ts";
import { trashcan } from "../common/icons.tsx";
import FieldDefaultValueEditor from "./defaultValue/FieldDefaultValueEditor.tsx";

interface ITemplateStepEditorProps {
    step: EntityTemplateStep;
    availableStandardFields: EntityTemplateStandardField[];
    availableCustomFields: Array<SelectableInputField>;
    isStandardTemplate: boolean;
    templateEntityType?: TemplateEntityType;
    computedFieldEditing: ComputedFieldTemplateEditing;
    // Why a field's trash icon is disabled, by field id; absent when it can be removed
    removeBlockedReasons: Record<string, string | undefined>;

    onFieldsReorder: (movedFieldId: string, newPosition: number) => void;
    onFieldDelete: (fieldId: string) => void;
    onFieldRequiredToggle: (fieldId: string, required: boolean) => void;
    onFieldRequiredAtCreationToggle: (fieldId: string, requiredAtCreation: boolean) => void;
    onFieldAdd: (field: EntityTemplateField) => void;
    // Left out when default values are not available, which hides their editor
    onFieldDefaultValueChange?: (fieldId: string, defaultValue: unknown) => void;
}

export const IconActionButtonsWrapper = styled(Box)(({ theme }) => ({
    display: "flex",
    width: "100%",
    justifyContent: "flex-end",
    marginTop: theme.spacing(2),
    gap: theme.spacing(1),
    alignItems: "center",
    minHeight: 40,
    "& svg": {
        height: 20,
        width: 20,
    },
}));

function TemplateStepEditor(props: ITemplateStepEditorProps) {
    const { translateString } = useTranslation();
    const { permissions } = useUserContext();

    const [addFieldAnchor, setAddFieldAnchor] = useState<null | HTMLElement>(null);
    const [lastAddedFieldId, setLastAddedFieldId] = useState<string | null>(null);

    useEffect(() => {
        if (lastAddedFieldId) {
            document.getElementById(lastAddedFieldId)?.scrollIntoView({ behavior: "smooth" });
        }
    }, [lastAddedFieldId, props.step.fields]);

    const handleDragEnd = (result: any) => {
        if (!result.destination) return;
        if (result.source.index === result.destination.index) return;

        const sortedFields = props.step.fields.sort((a, b) => a.order - b.order);
        const movedField = sortedFields[result.source.index];

        // Convert 0-based index to 1-based position
        const newPosition = result.destination.index + 1;

        props.onFieldsReorder(movedField.id, newPosition);
    };

    const handleDeleteField = (fieldId: string) => {
        const field = props.step.fields.find((f) => f.id === fieldId);
        if (field?.removeDisabled) {
            alert("This field is required and cannot be removed");
            return;
        }

        props.onFieldDelete(fieldId);
    };

    const handleRequiredToggle = (fieldId: string, isRequired: boolean) => {
        props.onFieldRequiredToggle(fieldId, isRequired);
    };

    const handleAddField = (standardField?: EntityTemplateStandardField, customField?: SelectableInputField) => {
        const newField: EntityTemplateField = {
            id: `field-${Date.now()}`,
            standardFieldIdentifier: standardField,
            customInputFieldId: customField?.id,
            customInputFieldName: customField?.name,
            customInputFieldType: customField?.type,
            customInputFieldDescription: customField?.description,
            order: props.step.fields.length + 1,
            required: false,
            removeDisabled: false,
            requiredAtCreation: false,
            requiredAtCreationDisabled: false,
            requiredDisabled: false,
            disabled: false,
        };

        setLastAddedFieldId(newField.id);
        props.onFieldAdd(newField);
        setAddFieldAnchor(null);
    };

    const renderFieldPreview = (field: EntityTemplateField) => {
        if (props.onFieldDefaultValueChange && field.defaultValueSupported && props.templateEntityType) {
            const onFieldDefaultValueChange = props.onFieldDefaultValueChange;
            return (
                <Box id={field.id}>
                    <FieldDefaultValueEditor
                        templateEntityType={props.templateEntityType}
                        field={field}
                        onChange={(defaultValue) => onFieldDefaultValueChange(field.id, defaultValue)}
                    />
                </Box>
            );
        }

        // Use the shared field renderer in read-only mode for preview
        if (props.templateEntityType === TemplateEntityType.Contract) {
            return (
                <Box id={field.id}>
                    <ContractFieldRenderer
                        field={field}
                        // Dummy props for preview
                        validationErrors={[]}
                        isLoading={false}
                        permissions={permissions}
                        onCustomFieldChange={() => {}}
                        readOnly // This makes it preview mode
                        isPreview
                        onChange={async () => {}}
                        onRefetchContract={() => {}}
                        computedFieldEditing={props.computedFieldEditing}
                    />
                </Box>
            );
        } else if (props.templateEntityType === TemplateEntityType.System) {
            return (
                <Box id={field.id}>
                    <SystemFieldRenderer
                        field={field}
                        validationErrors={[]}
                        isLoading={false}
                        onCustomFieldChange={() => {}}
                        isPreview
                        onChange={async () => {}}
                        onRefetchSystem={() => {}}
                        computedFieldEditing={props.computedFieldEditing}
                    />
                </Box>
            );
        } else if (props.templateEntityType === TemplateEntityType.LegalEntity) {
            return (
                <Box id={field.id}>
                    <LegalEntityFieldRenderer
                        field={field}
                        validationErrors={[]}
                        isLoading={false}
                        onCustomFieldChange={() => {}}
                        isPreview
                        onChange={async () => {}}
                        computedFieldEditing={props.computedFieldEditing}
                    />
                </Box>
            );
        } else if (props.templateEntityType === TemplateEntityType.IncidentLog) {
            return (
                <Box id={field.id}>
                    <IncidentLogFieldRenderer
                        field={field}
                        validationErrors={[]}
                        isLoading={false}
                        onCustomFieldChange={() => {}}
                        isPreview
                        onChange={async () => {}}
                        computedFieldEditing={props.computedFieldEditing}
                    />
                </Box>
            );
        } else if (props.templateEntityType === TemplateEntityType.Process) {
            return (
                <Box id={field.id}>
                    <ProcessFieldRenderer
                        field={field}
                        validationErrors={[]}
                        isLoading={false}
                        onCustomFieldChange={() => {}}
                        isPreview
                        onChange={async () => {}}
                        computedFieldEditing={props.computedFieldEditing}
                    />
                </Box>
            );
        }

        return undefined;
    };

    const isFieldAvailable = (standardField: EntityTemplateStandardField) => {
        return !props.step.fields.some((f) => f.standardFieldIdentifier === standardField);
    };

    const isCustomFieldAvailable = (customFieldId: string) => {
        return !props.step.fields.some((f) => f.customInputFieldId === customFieldId);
    };

    return (
        <StepSegment size="small">
            <Box sx={{ position: "relative", display: "flex", flexDirection: "column" }}>
                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="fields">
                        {(provided) => (
                            <Box {...provided.droppableProps} ref={provided.innerRef}>
                                {props.step.fields.length === 0 ? (
                                    <Paper sx={{ p: 4, textAlign: "center", bgcolor: "background.paper", boxShadow: "rgb(231, 231, 231) 0px 3px 10px" }}>
                                        <Typography color="text.secondary">{translateString("noFieldOnStepEmptyState")}</Typography>
                                    </Paper>
                                ) : (
                                    props.step.fields
                                        .sort((a, b) => a.order - b.order)
                                        .map((field, index) => (
                                            <Draggable key={field.id} draggableId={field.id} index={index} isDragDisabled={props.isStandardTemplate}>
                                                {(provided, snapshot) => (
                                                    <Paper
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        sx={{
                                                            mb: 2,
                                                            p: 2,
                                                            px: 5.5,
                                                            paddingTop: "30px",
                                                            bgcolor: "background.paper",
                                                            boxShadow: snapshot.isDragging ? 3 : "rgb(231, 231, 231) 0px 3px 10px",
                                                            position: "relative",
                                                            "&:hover": {
                                                                "& .field-actions": {
                                                                    opacity: 1,
                                                                },
                                                            },
                                                        }}
                                                    >
                                                        <Box sx={{ display: "flex", alignItems: "center", position: "relative" }}>
                                                            {!props.isStandardTemplate && (
                                                                <Box
                                                                    {...provided.dragHandleProps}
                                                                    sx={{
                                                                        cursor: "grab",
                                                                        position: "absolute",
                                                                        left: "50%",
                                                                        transform: "translateX(-50%)",
                                                                    }}
                                                                >
                                                                    <DragIndicator
                                                                        sx={(theme) => ({
                                                                            transform: "rotate(90deg)",
                                                                            fill: theme.palette.grey[500],
                                                                            height: 20,
                                                                            outline: "none",
                                                                        })}
                                                                    />
                                                                </Box>
                                                            )}

                                                            <Box sx={{ marginLeft: "auto" }}>
                                                                <DotLegalChip
                                                                    size={"small"}
                                                                    variant={"outlined"}
                                                                    color={field.standardFieldIdentifier ? Color.lightBlue : Color.green}
                                                                    hoverText={
                                                                        field.standardFieldIdentifier
                                                                            ? translateString("standardFieldMeaningTooltip")
                                                                            : translateString("customFieldMeaningTooltip")
                                                                    }
                                                                    value={
                                                                        field.standardFieldIdentifier
                                                                            ? translateString("standardField")
                                                                            : translateString("customField")
                                                                    }
                                                                />
                                                            </Box>
                                                        </Box>

                                                        <Box mt={2}>{renderFieldPreview(field)}</Box>

                                                        {field.configuration}

                                                        <Box>
                                                            <Divider sx={{ marginTop: 2, marginBottom: props.isStandardTemplate ? 2 : 0 }} />
                                                            <Box
                                                                sx={(theme) => ({
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                })}
                                                            >
                                                                <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
                                                                    <Box
                                                                        sx={(theme) => ({
                                                                            color: theme.palette.primary.dark,
                                                                            "& .MuiSwitch-root .MuiSwitch-track": {
                                                                                backgroundColor: theme.palette.grey[400],
                                                                            },
                                                                            "& .MuiSwitch-thumb": {
                                                                                color: theme.customPalette.white,
                                                                            },
                                                                            "& .MuiBox-root": {
                                                                                fontWeight: 400,
                                                                            },
                                                                        })}
                                                                    >
                                                                        <DotLegalTooltip
                                                                            text={
                                                                                props.isStandardTemplate
                                                                                    ? translateString("standardEntityTemplateFieldRequiredDisabledTooltip")
                                                                                    : field.requiredDisabled
                                                                                      ? translateString("requiredDisabledTooltip")
                                                                                      : translateString("requiredEntityTemplateFieldTooltip")
                                                                            }
                                                                        >
                                                                            <Box component={"span"}>
                                                                                <DotLegalSwitch
                                                                                    disabled={props.isStandardTemplate || field.requiredDisabled}
                                                                                    checked={field.required}
                                                                                    rightLabel={translateString("required")}
                                                                                    greyUncheckedTrack
                                                                                    onChange={() => {
                                                                                        handleRequiredToggle(field.id, !field.required);
                                                                                    }}
                                                                                />
                                                                            </Box>
                                                                        </DotLegalTooltip>
                                                                    </Box>

                                                                    {field.required && (
                                                                        <Box
                                                                            sx={(theme) => ({
                                                                                color: theme.palette.primary.dark,
                                                                                "& .MuiSwitch-root .MuiSwitch-track": {
                                                                                    backgroundColor: theme.palette.grey[400],
                                                                                },
                                                                                "& .MuiSwitch-thumb": {
                                                                                    color: theme.customPalette.white,
                                                                                },
                                                                                "& .MuiBox-root": {
                                                                                    fontWeight: 400,
                                                                                },
                                                                            })}
                                                                        >
                                                                            <DotLegalTooltip
                                                                                text={
                                                                                    props.isStandardTemplate
                                                                                        ? translateString("standardEntityTemplateFieldRequiredDisabledTooltip")
                                                                                        : field.requiredAtCreationDisabled
                                                                                          ? translateString("requiredAtCreationDisabledTooltip")
                                                                                          : translateString("requiredAtCreationTooltip")
                                                                                }
                                                                            >
                                                                                <Box component={"span"}>
                                                                                    <DotLegalSwitch
                                                                                        disabled={props.isStandardTemplate || field.requiredAtCreationDisabled}
                                                                                        checked={field.requiredAtCreation}
                                                                                        rightLabel={translateString("requiredAtCreation")}
                                                                                        greyUncheckedTrack
                                                                                        onChange={() => {
                                                                                            props.onFieldRequiredAtCreationToggle(
                                                                                                field.id,
                                                                                                !field.requiredAtCreation
                                                                                            );
                                                                                        }}
                                                                                    />
                                                                                </Box>
                                                                            </DotLegalTooltip>
                                                                        </Box>
                                                                    )}
                                                                </Box>

                                                                {!props.isStandardTemplate && (
                                                                    <IconActionButtonsWrapper>
                                                                        <Box component={"span"}>
                                                                            {props.removeBlockedReasons[field.id] ? (
                                                                                <Box sx={{ "& svg": { opacity: 0.5 } }}>
                                                                                    <DotLegalTooltip text={props.removeBlockedReasons[field.id]}>
                                                                                        {trashcan}
                                                                                    </DotLegalTooltip>
                                                                                </Box>
                                                                            ) : (
                                                                                <DotLegalTooltip text={translateString("removeFieldFromTemplate")}>
                                                                                    <IconButton
                                                                                        sx={{ padding: 0 }}
                                                                                        size="small"
                                                                                        onClick={() => handleDeleteField(field.id)}
                                                                                        color="error"
                                                                                    >
                                                                                        {trashcan}
                                                                                    </IconButton>
                                                                                </DotLegalTooltip>
                                                                            )}
                                                                        </Box>
                                                                    </IconActionButtonsWrapper>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    </Paper>
                                                )}
                                            </Draggable>
                                        ))
                                )}
                                {provided.placeholder}
                            </Box>
                        )}
                    </Droppable>
                </DragDropContext>

                {!props.isStandardTemplate && (props.availableCustomFields.length > 0 || props.availableStandardFields.length > 0) && (
                    <Box sx={{ position: "sticky", height: 0, width: "100%", zIndex: 100, bottom: 0 }}>
                        <Box sx={{ position: "absolute", right: -105, bottom: 16 }}>
                            <DotLegalButton buttonType={"primary"} onClick={(e) => setAddFieldAnchor(e.currentTarget)}>
                                {translateString("addField")}
                            </DotLegalButton>
                        </Box>
                    </Box>
                )}
            </Box>

            <AddFieldPopover
                anchorEl={addFieldAnchor}
                onClose={() => setAddFieldAnchor(null)}
                availableStandardFields={props.availableStandardFields.filter(isFieldAvailable)}
                availableCustomFields={props.availableCustomFields.filter((cf) => isCustomFieldAvailable(cf.id))}
                onFieldAdd={handleAddField}
            />
        </StepSegment>
    );
}

export default TemplateStepEditor;
